import { Component, Injector, OnDestroy, ViewChild } from '@angular/core';
import { FormComponent } from 'src/app/core/abstracts/components/form.component';
import { TabEnum } from 'src/app/core/enums/tab.enum';
import { RegexPatternValidator } from '../../core/validators/regex-pattern.validator';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {UntypedFormArray, UntypedFormControl, Validators} from '@angular/forms';
import { KindComponent } from '../kind/kind.component';
import { Mitteilung } from '../../core/models/mitteilung';
import { KindMiteilung } from '../../core/models/kind';
import { PiwikService } from 'src/app/shared/services/piwik';
import { IPxErrorBoxComponent } from 'src/app/shared/library/px-ui/px-forms/components';
import { DateValidator, StaticCommonValidator } from 'src/app/shared/library/px-ui/px-forms/validators';
import { DateUtil, ObjectUtil } from 'src/app/shared/library/px-ui/px-forms/utils';
import {kgNrValidator, nameValidator} from "../../core/validators/validators";

@Component({
    selector: 'app-veraenderungsmitteilung',
    templateUrl: './veraenderungsmitteilung.component.html',
    styleUrls: ['./veraenderungsmitteilung.component.scss'],
})
export class VeraenderungsmitteilungComponent extends FormComponent implements OnDestroy {
    private subSink$ = new Subscription();
    gesendet = false;
    @ViewChild('fehlerboxComponent') fehlerbox: IPxErrorBoxComponent;
    veraenderungNachrichtLabel = 'Eintrittsdatum';
    zahlenstellenNummerLabel = 'Ordnungskriterium des Arbeitgebers';
    isOrdnungskriteriumSelected = false;

    constructor(private injector: Injector, private matomoService: PiwikService) {
        super(injector);
        this.tabService.setCurrentTab(TabEnum.VERAENDERUNGSMITTEILUNG);
        this.mappingCandidates = {
            mitarbeiterGeburtsdatum: this.dateMappingProvider,
            veraenderungsdatum: this.dateMappingProvider,
        };
        this.init();
    }

    init() {
        this.initFormGroup(this.newVeraenderungsFormGroup());
        this.formGroup.addControl('kinder', new UntypedFormArray([KindComponent.newKindFormGroup()]));
        this.subSink$.add(
            this.formGroup.get('veraenderungsart').valueChanges.subscribe((value) => this.switchVeraenderungsart(value))
        );
        this.veraenderungNachrichtLabel = 'Eintrittsdatum';
        this.gesendet = false;
        this.storeService.deleteData();
    }

    get veraenderungsart() {
        return this.formGroup.get('veraenderungsart').value;
    }

    private switchVeraenderungsart(value) {
        if (this.formGroup.get('ordnungkriteriumNew')) {
            this.formGroup.removeControl('ordnungkriteriumNew');
            this.formGroup.addControl('kinder', new UntypedFormArray([KindComponent.newKindFormGroup()]));
        }
        this.zahlenstellenNummerLabel = 'Ordnungskriterium des Arbeitgebers';
        this.isOrdnungskriteriumSelected = false;
        switch (value) {
            case 'EINTRITT':
                this.veraenderungNachrichtLabel = 'Eintrittsdatum';
                this.formGroup.get('kindergeldnummer').setValidators(kgNrValidator);
                this.formGroup.get('kindergeldnummer').updateValueAndValidity();
                break;
            case 'AUSTRITT':
                this.veraenderungNachrichtLabel = 'Austrittsdatum';
                this.formGroup
                    .get('kindergeldnummer')
                    .setValidators([StaticCommonValidator.required, kgNrValidator]);
                this.formGroup.get('kindergeldnummer').updateValueAndValidity();
                break;
            /* KINDERPLUS und KINDERMINUS haben dieselben Änderungen und sind bewusst zusammen gefasst */
            case 'KINDERPLUS':
            case 'KINDERMINUS':
                this.veraenderungNachrichtLabel = 'Änderung gültig ab';
                this.formGroup
                    .get('kindergeldnummer')
                    .setValidators([StaticCommonValidator.required, kgNrValidator]);
                this.formGroup.get('kindergeldnummer').updateValueAndValidity();
                break;
            case 'ORDNUNGSKRITERIUM':
                this.isOrdnungskriteriumSelected = true;
                this.veraenderungNachrichtLabel = 'Änderung gültig ab';
                this.zahlenstellenNummerLabel = 'bisheriges Ordnungskriterium des Arbeitsgebers (alt)';
                this.formGroup
                    .get('kindergeldnummer')
                    .setValidators([StaticCommonValidator.required, kgNrValidator]);
                this.formGroup.get('kindergeldnummer').updateValueAndValidity();
                this.formGroup.addControl(
                    'ordnungkriteriumNew',
                    new UntypedFormControl('', [StaticCommonValidator.required, RegexPatternValidator.ordnungkriterium])
                );
                this.formGroup.get('ordnungkriteriumNew').updateValueAndValidity();
                this.formGroup.removeControl('kinder');
                break;
            default:
                throw new Error('Unexpected switch-case default call');
        }
    }

    private newVeraenderungsFormGroup() {
        return {
            veraenderungsart: ['EINTRITT', [StaticCommonValidator.requiredRadio]],
            veraenderungsdatum: ['', [StaticCommonValidator.required, DateValidator.valid]],
            mitarbeiterNachname: ['', [Validators.required, nameValidator]],
            mitarbeiterVorname: ['', [Validators.required, nameValidator]],
            mitarbeiterGeburtsdatum: [
                '',
                [
                    StaticCommonValidator.required,
                    DateValidator.valid,
                    DateValidator.geburtsdatum,
                    RegexPatternValidator.geburtsdatum,
                ],
            ],
            kindergeldnummer: ['', [kgNrValidator]],
            ordnungkriterium: ['', [StaticCommonValidator.required, RegexPatternValidator.ordnungkriterium]],
            veraenderungNachricht: [''],
            erklaerung: ['', [StaticCommonValidator.requiredCheckbox]],
        };
    }

    private resetMitteilung() {
        this.kinder.clear();
        this.formGroup.reset({
            veraenderungsart: '',
            veraenderungsdatum: '',
            mitarbeiterNachname: '',
            mitarbeiterVorname: '',
            mitarbeiterGeburtsdatum: '',
            kindergeldnummer: '',
            ordnungkriterium: '',
            veraenderungNachricht: '',
            erklaerung: '',
        });
        this.kinder.push(KindComponent.newKindFormGroup());
    }

    get kinder() {
        return this.formGroup.get('kinder') as UntypedFormArray;
    }

    absenden() {
        if (this.formGroup.valid) {
            const mitteilung = this.mapFormToJson();
            /* Wenn veraenderungsart == 'ORDNUNGSKRITERIUM' dann anderer Endpunkt, */
            /* weil Daten für Backend zu unterschiedlich sind */
            if (this.isOrdnungskriteriumSelected) {
                this.subSink$.add(
                    this.saveWithBody('/ordnungkriteriumAendern', mitteilung).subscribe(
                        (file) => {
                            this.storeService.setData(mitteilung, 'mitteilung');
                            this.storeService.setData(file, 'file');
                            this.gesendet = true;
                            this.matomoService.trackEvent('buttonKlicken', 'Veränderungsmitteilung absenden');
                        },
                        (error: HttpErrorResponse) => {
                            this.httpErrorHandling(error);
                        }
                    )
                );
            } else {
                this.subSink$.add(
                    this.saveWithBody('/veraenderungsmitteilungAbsenden', mitteilung).subscribe(
                        (file) => {
                            this.storeService.setData(mitteilung, 'mitteilung');
                            this.storeService.setData(file, 'file');
                            this.storeService.setData(mitteilung.kinder, 'kinderMitteilung');
                            this.gesendet = true;
                            this.matomoService.trackEvent('buttonKlicken', 'Veränderungsmitteilung absenden');
                        },
                        (error: HttpErrorResponse) => {
                            this.httpErrorHandling(error);
                        }
                    )
                );
            }
        } else {
            this.formGroup.markAllAsTouched();
            this.fehlerbox.validate(this.formGroup);
        }
    }

    private httpErrorHandling(error) {
        this.routerService.goToErrorPage();
        this.matomoService.trackEvent('buttonKlicken', 'Veränderungsmitteilung fehlgeschlagen', error.status);
    }

    private mapFormToJson() {
        const miteilung: Mitteilung = { ...this.formGroup.value };
        miteilung.kindergeldnummer = ObjectUtil.isEmpty(miteilung.kindergeldnummer) ? null : miteilung.kindergeldnummer;
        miteilung.mitarbeiterGeburtsdatum = DateUtil.toService(miteilung.mitarbeiterGeburtsdatum);
        miteilung.veraenderungsdatum = DateUtil.toService(miteilung.veraenderungsdatum);
        if (!this.isOrdnungskriteriumSelected) {
            const kinder: KindMiteilung[] = [];
            miteilung.kinder.forEach((kind) => {
                kinder.push({
                    kindGeburtsdatum: DateUtil.toService(kind.kindGeburtsdatum),
                    kindVorname: kind.kindVorname,
                    kindAbweichenderNachname: kind.kindAbweichenderNachname,
                });
            });
            miteilung.kinder = kinder;
        }
        return miteilung;
    }

    formularLeeren() {
        this.resetMitteilung();
        this.fehlerbox.reset();
    }

    ngOnDestroy(): void {
        this.subSink$.unsubscribe();
    }
}
